import {
    IonCheckbox,
    IonItem,
    IonLabel,
    IonList,
    IonRadio,
    IonRadioGroup,
    IonTextarea,
} from '@ionic/react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContactReportReason } from '../../../lib/apollo/types'
import { useReportContactUserMutation } from '../../../lib/apollo/types'
import useToast from '../../../providers/Toast/hooks'
import Button from '../../Button'
import Buttons from '../../Buttons'
import Modal from '../../Modal'

import './style.scss'

/** Spiegelt `MAX_REPORT_NOTE_LENGTH` der Middleware — dort wird es erzwungen. */
export const MAX_REPORT_NOTE_LENGTH = 1000

/**
 * Die Gruende in der Reihenfolge, in der sie gebraucht werden. "Sonstiges"
 * steht bewusst am Ende: Wer einen der benannten Gruende findet, liefert der
 * Moderation die brauchbarere Meldung.
 */
const REASONS: ContactReportReason[] = [
    ContactReportReason.Spam,
    ContactReportReason.Harassment,
    ContactReportReason.FakeProfile,
    ContactReportReason.Inappropriate,
    ContactReportReason.Other,
]

const REASON_LABELS: Record<ContactReportReason, [string, string]> = {
    [ContactReportReason.Spam]: ['contact.report.reason.spam', 'Spam oder Werbung'],
    [ContactReportReason.Harassment]: [
        'contact.report.reason.harassment',
        'Belästigung oder Beleidigung',
    ],
    [ContactReportReason.FakeProfile]: [
        'contact.report.reason.fakeProfile',
        'Gefälschtes Profil',
    ],
    [ContactReportReason.Inappropriate]: [
        'contact.report.reason.inappropriate',
        'Unangemessene Inhalte',
    ],
    [ContactReportReason.Other]: ['contact.report.reason.other', 'Sonstiges'],
}

interface ContactReportModalProps {
    isOpen: boolean
    onClose: () => void
    conversationId: string
    /** Wen man meldet — damit niemand die falsche Person erwischt. */
    partnerName?: string | null
    /**
     * Wird nur gerufen, wenn zusaetzlich blockiert wurde: Die Unterhaltung ist
     * danach nicht mehr erreichbar, die Seite muss sie verlassen.
     */
    onBlocked?: () => void
}

/**
 * Melden und Blockieren sind zwei Dinge, aber selten zwei Wuensche: Wer sich
 * die Muehe einer Meldung macht, will die Person meist auch nicht mehr hoeren.
 * Deshalb steht die Blockierung hier als voreingestellter Haken und nicht als
 * zweiter Weg durch ein zweites Menue.
 */
const ContactReportModal: React.FC<ContactReportModalProps> = ({
    isOpen,
    onClose,
    conversationId,
    partnerName,
    onBlocked,
}) => {
    const { t } = useTranslation()
    const [present] = useToast()
    const [reason, setReason] = useState<ContactReportReason | undefined>()
    const [note, setNote] = useState('')
    const [block, setBlock] = useState(true)

    const [report, { loading }] = useReportContactUserMutation({
        // Die Blockierung nimmt die Unterhaltung aus beiden Listen — ohne das
        // stuende sie dort weiter, bis die App neu geladen wird.
        refetchQueries: [
            'contactConversations',
            'contactUnread',
            'contactRequests',
            'contactRequestsWithPeople',
            'contactBlockedUsers',
        ],
        onError: () => {
            present(
                t(
                    'contact.report.failed',
                    'Die Meldung konnte nicht gesendet werden.',
                ),
                'danger',
            )
        },
    })

    const close = useCallback(() => {
        setReason(undefined)
        setNote('')
        setBlock(true)
        onClose()
    }, [onClose])

    const submit = useCallback(async () => {
        if (!reason) return

        const { data } = await report({
            variables: {
                input: {
                    conversationId,
                    reason,
                    note: note.trim() || undefined,
                    block,
                },
            },
        })

        if (!data) return

        present(
            block
                ? t(
                    'contact.report.sentAndBlocked',
                    'Danke. Wir sehen uns die Meldung an, und du bekommst von diesem Kontakt nichts mehr.',
                )
                : t(
                    'contact.report.sent',
                    'Danke. Wir sehen uns die Meldung an.',
                ),
            'success',
        )

        close()

        if (block) {
            onBlocked?.()
        }
    }, [reason, report, conversationId, note, block, present, t, close, onBlocked])

    return (
        <Modal
            modalTitle={t('contact.report.title', 'Profil melden')}
            isOpen={isOpen}
            onDidDismiss={close}
            onClose={close}
            size='alert'
        >
            <div className='contact-report ion-padding-horizontal'>
                <p className='n2br contact-report__hint'>
                    {partnerName
                        ? t('contact.report.hintNamed', {
                            defaultValue:
                                  'Du meldest {{name}}. Die Meldung geht an unser Team, die gemeldete Person erfährt nichts davon.',
                            name: partnerName,
                        })
                        : t(
                            'contact.report.hint',
                            'Die Meldung geht an unser Team, die gemeldete Person erfährt nichts davon.',
                        )}
                </p>

                <IonList className='contact-report__reasons'>
                    <IonRadioGroup
                        value={reason}
                        onIonChange={(event) => setReason(event.detail.value)}
                    >
                        {REASONS.map((value) => {
                            const [key, fallback] = REASON_LABELS[value]

                            return (
                                <IonItem
                                    key={value}
                                    lines='none'
                                >
                                    <IonLabel className='ion-text-wrap'>
                                        {t(key, fallback)}
                                    </IonLabel>
                                    <IonRadio
                                        slot='start'
                                        value={value}
                                    />
                                </IonItem>
                            )
                        })}
                    </IonRadioGroup>
                </IonList>

                <IonTextarea
                    className='contact-report__input'
                    rows={4}
                    value={note}
                    maxlength={MAX_REPORT_NOTE_LENGTH}
                    placeholder={t(
                        'contact.report.notePlaceholder',
                        'Was ist passiert? (optional)',
                    )}
                    // Wie im Composer: `onIonChange` feuert beim Textarea erst
                    // beim Verlassen des Feldes.
                    onIonInput={(event) => setNote(event.detail.value ?? '')}
                />

                <IonItem
                    className='contact-report__block'
                    lines='none'
                >
                    <IonCheckbox
                        checked={block}
                        onIonChange={(event) => setBlock(event.detail.checked)}
                    >
                        <IonLabel className='ion-text-wrap'>
                            {t(
                                'contact.report.alsoBlock',
                                'Diesen Kontakt zusätzlich blockieren',
                            )}
                        </IonLabel>
                    </IonCheckbox>
                </IonItem>
            </div>
            <Buttons>
                <Button
                    fill='solid'
                    color='secondary'
                    loading={loading}
                    disabled={!reason || loading}
                    onClick={submit}
                >
                    {t('contact.report.submit', 'Meldung senden')}
                </Button>
                <Button
                    fill='clear'
                    onClick={close}
                >
                    {t('buttons.cancel')}
                </Button>
            </Buttons>
        </Modal>
    )
}

export default ContactReportModal
